import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRecommendation = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [formData, setFormData] = useState({
    roomSize: '',
    usageLevel: '',
    insulation: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.roomSize || !formData.usageLevel || !formData.insulation) {
      toast({
        title: "Eksik Bilgi",
        description: "Lutfen tum alanlari doldurun",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai-recommendation`, formData);
      setRecommendation(response.data);
      toast({
        title: "Oneri Hazir!",
        description: `${response.data.recommendations.length} farkli model onerisi olusturuldu`
      });
    } catch (error) {
      console.error('AI recommendation error:', error);
      toast({
        title: "Hata",
        description: "Oneri olusturulamadi. Lutfen tekrar deneyin.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Klima Önerisi</h1>
          <p className="text-xl text-gray-600">
            Yapay zeka destekli sistemimiz, ihtiyaciniza en uygun klima modelini onerir
          </p>
        </div>

        <Card className="border-none shadow-xl mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Bilgilerinizi Girin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="roomSize" className="text-base font-semibold">Oda Buyuklugu (m2)</Label>
                <Input
                  id="roomSize"
                  type="number"
                  placeholder="Orn: 25"
                  value={formData.roomSize}
                  onChange={(e) => setFormData({ ...formData, roomSize: e.target.value })}
                  className="mt-2 h-12"
                />
              </div>

              <div>
                <Label htmlFor="usageLevel" className="text-base font-semibold">Kullanim Seviyesi</Label>
                <Select
                  value={formData.usageLevel}
                  onValueChange={(value) => setFormData({ ...formData, usageLevel: value })}
                >
                  <SelectTrigger className="mt-2 h-12">
                    <SelectValue placeholder="Kullanim seviyesi secin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dusuk">Dusuk (Ara sira kullanim)</SelectItem>
                    <SelectItem value="Orta">Orta (Gunluk birkac saat)</SelectItem>
                    <SelectItem value="Yuksek">Yuksek (Gece gunduz)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="insulation" className="text-base font-semibold">Yalitim Durumu</Label>
                <Select
                  value={formData.insulation}
                  onValueChange={(value) => setFormData({ ...formData, insulation: value })}
                >
                  <SelectTrigger className="mt-2 h-12">
                    <SelectValue placeholder="Yalitim durumu secin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Zayif">Zayif</SelectItem>
                    <SelectItem value="Orta">Orta</SelectItem>
                    <SelectItem value="Iyi">Iyi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Öneri Hazırlanıyor...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Öneri Al
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recommendation Results - Multiple Models */}
        {recommendation && recommendation.recommendations && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center">
                <Sparkles className="mr-2 text-teal-600" />
                Önerileriniz
              </h2>
              <p className="text-gray-600">Size uygun {recommendation.recommendations.length} farkli klima modeli bulduk</p>
            </div>

            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
              {recommendation.recommendations.map((rec, index) => (
                <Card key={index} className="border-none shadow-xl bg-gradient-to-br from-teal-50 to-blue-50 hover:shadow-2xl transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-teal-600">Oneri #{index + 1}</span>
                      <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {rec.energyClass}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{rec.model}</CardTitle>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-600">Oda: {rec.roomSize}</p>
                      <p className="text-xs text-gray-600">{rec.estimatedConsumption}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">Neden Bu Model?</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{rec.reason}</p>
                    </div>

                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 mb-3 text-sm">Ozellikler:</h4>
                      <ul className="space-y-2">
                        {rec.features && rec.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                            <span className="text-gray-700 text-xs">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button className="w-full bg-teal-500 hover:bg-teal-600 text-white">
                      Bu Modeli Sec
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button
                size="lg"
                variant="outline"
                className="border-teal-500 text-teal-600 hover:bg-teal-50"
                onClick={() => setRecommendation(null)}
              >
                Yeni Oneri Al
              </Button>
            </div>
          </div>
        )}

        <Card className="mt-8 border-none shadow-md bg-gray-50">
          <CardContent className="p-6">
            <h4 className="font-bold text-gray-900 mb-2">Nasil Calisir?</h4>
            <p className="text-gray-600 text-sm leading-relaxed">
              Yapay zeka destekli oneri sistemimiz, odanizin buyuklugunu, kullanim aliskanliklarinizi ve 
              yalitim durumunu analiz ederek size en uygun klima modelini onerir. Onerimiz, enerji 
              verimliligi, sogutma kapasitesi ve maliyet dengesi gozetilerek olusturulur.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIRecommendation;
